import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { usePageMeta } from "@/hooks/usePageMeta";
import { siteIntro } from "@/mocks/videos";

const services = [
  {
    icon: "ri-play-circle-line",
    title: "短视频浏览",
    desc: "海量热门短视频随心观看，推荐算法帮你快速发现感兴趣的内容。",
  },
  {
    icon: "ri-share-forward-line",
    title: "分享与互动",
    desc: "点赞、评论、收藏、分享，和喜欢的创作者及其他用户实时互动。",
  },
  {
    icon: "ri-upload-cloud-2-line",
    title: "创作与发布",
    desc: "人人都是创作者，用镜头记录生活，让你的作品被更多人看到。",
  },
  {
    icon: "ri-medal-line",
    title: "创作者扶持",
    desc: "流量推荐、创作课程与激励计划，陪伴每一位创作者持续成长。",
  },
];

const terms = [
  {
    title: "使用条款",
    content:
      "本网站为功能演示站点，所有视频、作者及数据均为示例内容，不构成任何真实服务承诺。用户在使用过程中请遵守当地法律法规，尊重他人权益，不得发布违法违规或侵权内容。",
  },
  {
    title: "隐私政策",
    content:
      "演示站点不收集、不存储任何个人信息。所有浏览与互动行为仅在本机浏览器内进行，不会上传至服务器，请放心体验。",
  },
  {
    title: "内容规范",
    content:
      "我们倡导真实、温暖、有创意的表达。鼓励原创内容，反对抄袭搬运、低俗暴力与虚假信息，共同维护健康的社区氛围。",
  },
];

export default function AboutPage() {
  usePageMeta({
    title: "关于与帮助 - 快手短视频平台",
    description:
      "了解快手短视频平台定位、核心服务、使用条款与隐私政策，以及如何联系我们与反馈建议，共同构建温暖友善的创作社区。",
    keywords: "快手介绍,短视频平台,使用条款,隐私政策,联系快手,帮助中心",
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1 pt-16">
        <section className="w-full bg-gradient-to-br from-primary-50 to-background-100 border-b border-background-200">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-12 md:py-16">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs mb-4">
              <i className="ri-information-line"></i>
              关于快手
            </span>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-950">
              {siteIntro.subtitle}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 mt-4 max-w-3xl leading-relaxed">
              {siteIntro.paragraphs[0]}
            </p>
          </div>
        </section>

        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14 space-y-14">
          <section>
            <h2 className="text-2xl font-bold text-foreground-950 mb-6">我们提供什么</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {services.map((s) => (
                <div key={s.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className="w-12 h-12 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center text-2xl mb-4">
                    <i className={s.icon}></i>
                  </span>
                  <h3 className="text-base font-bold text-foreground-900">{s.title}</h3>
                  <p className="text-sm text-foreground-600 mt-2 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-xl bg-background-100 border border-background-200 p-6 md:p-10">
            <h2 className="text-2xl font-bold text-foreground-950 mb-4">平台定位与服务内容</h2>
            <div className="space-y-3 max-w-4xl">
              {siteIntro.paragraphs.map((p, i) => (
                <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground-950 mb-6">使用条款与隐私政策</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {terms.map((t) => (
                <div key={t.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                  <h3 className="text-base font-bold text-foreground-900 flex items-center gap-2 mb-3">
                    <i className="ri-file-text-line text-accent-500"></i>
                    {t.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                  <span className="inline-flex items-center gap-1 text-xs text-accent-600 mt-4 cursor-pointer hover:text-accent-700">
                    查看详情
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-xl border border-background-200 bg-background-50 p-6 md:p-10">
            <h2 className="text-2xl font-bold text-foreground-950 mb-6">联系方式与反馈</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-start gap-3">
                <span className="w-11 h-11 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center text-xl shrink-0">
                  <i className="ri-customer-service-2-line"></i>
                </span>
                <div>
                  <h3 className="text-sm font-bold text-foreground-900">在线客服</h3>
                  <p className="text-sm text-foreground-600 mt-1">
                    演示站点暂未提供真实在线客服，正式上线后可通过应用内入口联系我们。
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-11 h-11 rounded-lg bg-accent-100 text-accent-600 flex items-center justify-center text-xl shrink-0">
                  <i className="ri-mail-line"></i>
                </span>
                <div>
                  <h3 className="text-sm font-bold text-foreground-900">意见反馈</h3>
                  <p className="text-sm text-foreground-600 mt-1">
                    如对平台有任何建议或问题，正式上线后可通过反馈入口提交，我们会认真查看。
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-11 h-11 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center text-xl shrink-0">
                  <i className="ri-question-answer-line"></i>
                </span>
                <div>
                  <h3 className="text-sm font-bold text-foreground-900">帮助中心</h3>
                  <p className="text-sm text-foreground-600 mt-1">
                    常见问题与使用指南整理中，后续将陆续上线，敬请期待。
                  </p>
                </div>
              </div>
            </div>
            <p className="text-xs text-foreground-400 mt-6">
              * 本页面为信息展示页，所有联系方式为文字说明，未提供真实联系服务。
            </p>
          </section>

          <section className="rounded-xl bg-gradient-to-br from-accent-500 to-accent-600 px-6 py-10 md:px-12 md:py-14 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-background-50">
              加入快手，一起记录每一种生活
            </h2>
            <p className="text-sm md:text-base text-background-50/85 mt-3 max-w-2xl mx-auto">
              无论你是观众还是创作者，这里都有属于你的舞台。现在就开启你的短视频之旅吧。
            </p>
            <div className="flex flex-wrap justify-center gap-3 mt-6">
              <Link
                to="/"
                className="flex items-center gap-2 h-11 px-6 rounded-full bg-background-50 text-accent-600 text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-compass-3-line"></i>
                去发现视频
              </Link>
              <Link
                to="/creator"
                className="flex items-center gap-2 h-11 px-6 rounded-full border border-background-50/60 text-background-50 text-sm font-medium hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-upload-2-line"></i>
                进入创作者中心
              </Link>
            </div>
          </section>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}